



import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import logo from '../assets/images/Login.png';

// Define all animations at the top
const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
  50% { transform: translateY(-5px) rotate(1deg) scale(1.01); }
`;

const oceanWaves = keyframes`
  0% { 
    background-position: 0% 0%;
    filter: hue-rotate(0deg) brightness(1);
  }
  50% { 
    background-position: 100% 100%;
    filter: hue-rotate(5deg) brightness(1.1);
  }
  100% { 
    background-position: 0% 0%;
    filter: hue-rotate(0deg) brightness(1);
  }
`;

const blueAurora = keyframes`
  0%, 100% { 
    opacity: 0.2;
    transform: scale(1) rotate(0deg);
    filter: blur(15px);
  }
  50% { 
    opacity: 0.4;
    transform: scale(1.1) rotate(180deg);
    filter: blur(20px);
  }
`;

const crystalSpin = keyframes`
  0% { 
    transform: rotateY(0deg) rotateX(0deg) scale(1);
  }
  50% { 
    transform: rotateY(180deg) rotateX(10deg) scale(1.02);
  }
  100% { 
    transform: rotateY(360deg) rotateX(0deg) scale(1);
  }
`;

const rippleEffect = keyframes`
  0% { 
    transform: scale(0.9);
    opacity: 0.8;
  }
  100% { 
    transform: scale(1.8);
    opacity: 0;
  }
`;

const floatParticles = keyframes`
  0% { 
    transform: translateY(100vh) rotate(0deg) scale(0);
    opacity: 0;
  }
  50% { 
    opacity: 0.5;
    transform: translateY(50vh) rotate(180deg) scale(1);
  }
  100% { 
    transform: translateY(-20vh) rotate(360deg) scale(0);
    opacity: 0;
  }
`;

const neonPulse = keyframes`
  0%, 100% { 
    text-shadow: 0 0 20px rgba(33,150,243,0.5);
  }
  50% { 
    text-shadow: 0 0 40px rgba(33,150,243,0.8);
  }
`;

const shimmerBlue = keyframes`
  0% { 
    background-position: -200% center;
  }
  100% { 
    background-position: 200% center;
  }
`;

const rotate3D = keyframes`
  0% { 
    transform: perspective(800px) rotateY(0deg);
  }
  50% { 
    transform: perspective(800px) rotateY(5deg);
  }
  100% { 
    transform: perspective(800px) rotateY(0deg);
  }
`;

interface SplashScreenProps {
  duration?: number;
  onComplete?: () => void;
  userName?: string;
}

const PremiumBlueSplashScreen: React.FC<SplashScreenProps> = ({ 
  duration = 3500, 
  onComplete,
  userName 
}) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  // Premium blue-themed particles
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 6 + Math.random() * 4,
    size: 8 + Math.random() * 20,
    type: Math.floor(Math.random() * 4),
    rotation: Math.random() * 360,
    opacity: 0.2 + Math.random() * 0.3,
  }));

  // Styles object with reduced padding/margins
  const styles = {
    container: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      alignItems: 'center',
      background: 'radial-gradient(circle at 30% 50%, #0a1929 0%, #0d2135 50%, #0a1929 100%)',
      backgroundSize: '200% 200%',
      animation: `${oceanWaves} 10s ease infinite`,
      overflow: 'hidden',
      zIndex: 9999,
      fontFamily: '"Segoe UI", "Roboto", sans-serif',
    },

    aurora1: {
      position: 'absolute' as const,
      top: '5%',
      left: '5%',
      width: '60%',
      height: '60%',
      background: 'radial-gradient(circle at 30% 50%, rgba(33,150,243,0.15) 0%, transparent 50%)',
      animation: `${blueAurora} 8s ease-in-out infinite`,
      pointerEvents: 'none' as const,
    },

    aurora2: {
      position: 'absolute' as const,
      bottom: '5%',
      right: '5%',
      width: '50%',
      height: '50%',
      background: 'radial-gradient(circle at 70% 30%, rgba(100,181,246,0.1) 0%, transparent 40%)',
      animation: `${blueAurora} 10s ease-in-out infinite reverse`,
      pointerEvents: 'none' as const,
    },

    gridOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `
        linear-gradient(rgba(33,150,243,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(33,150,243,0.03) 1px, transparent 1px)
      `,
      backgroundSize: '40px 40px',
      animation: `${rotate3D} 15s linear infinite`,
      pointerEvents: 'none' as const,
    },

    content: {
      position: 'relative' as const,
      zIndex: 10,
      textAlign: 'center' as const,
      animation: `${fadeIn} 1s ease`,
      padding: { xs: '0.5rem', sm: '1rem', md: '1.5rem' },
      maxWidth: { xs: '95%', sm: '90%', md: '550px' },
      width: '100%',
    },

    welcomeSection: {
      marginBottom: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
      animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
      position: 'relative' as const,
    },

    welcomeGlowRing: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '120%',
      height: '120%',
      background: 'radial-gradient(circle, rgba(33,150,243,0.2) 0%, transparent 70%)',
      animation: `${rippleEffect} 3s ease-out infinite`,
      pointerEvents: 'none' as const,
    },

    welcomeText: {
      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
      fontWeight: 700,
      background: 'linear-gradient(135deg, #fff, #90caf9, #42a5f5)',
      backgroundSize: '200% 200%',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: `${neonPulse} 2s ease-in-out infinite`,
      marginBottom: '0.3rem',
      fontFamily: '"Playfair Display", serif',
      letterSpacing: '2px',
      position: 'relative' as const,
      zIndex: 2,
      textTransform: 'uppercase' as const,
    },

    personalizedWelcome: {
      fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
      fontWeight: 600,
      background: 'linear-gradient(135deg, #64b5f6, #1976d2)',
      backgroundSize: '200% 200%',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: `${oceanWaves} 4s ease infinite`,
      marginBottom: '0.5rem',
      fontStyle: 'italic',
    },

    tagline: {
      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: 300,
      animation: `${fadeInUp} 0.8s ease-out 0.4s both`,
      letterSpacing: '1px',
      maxWidth: '450px',
      margin: '0 auto',
      lineHeight: 1.4,
      textShadow: '0 0 15px rgba(33,150,243,0.3)',
    },

    logoSection: {
      position: 'relative' as const,
      marginBottom: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      animation: `${fadeInUp} 0.8s ease-out 0.6s both`,
    },

    logoContainer: {
      position: 'relative' as const,
      width: { xs: '80px', sm: '90px', md: '100px' },
      height: { xs: '80px', sm: '90px', md: '100px' },
      marginBottom: '0.8rem',
      transformStyle: 'preserve-3d' as const,
      animation: `${crystalSpin} 6s ease-in-out infinite`,
    },

    logoRing1: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '180%',
      height: '180%',
      border: '1px solid rgba(33,150,243,0.2)',
      borderRadius: '50%',
      animation: `${crystalSpin} 8s linear infinite reverse`,
    },

    logoRing2: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '160%',
      height: '160%',
      border: '1px dashed rgba(33,150,243,0.3)',
      borderRadius: '50%',
      animation: `${crystalSpin} 6s linear infinite`,
    },

    logoGlow: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(33,150,243,0.3) 0%, transparent 70%)',
      borderRadius: '50%',
      animation: `${rippleEffect} 3s ease-out infinite`,
      filter: 'blur(15px)',
    },

    logo: {
      width: '100%',
      height: '100%',
      animation: `${float} 3s ease-in-out infinite`,
      filter: 'drop-shadow(0 0 30px rgba(33,150,243,0.5)) brightness(1.2)',
      borderRadius: '30%',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,255,255,0.9))',
      padding: '12px',
      boxSizing: 'border-box' as const,
      position: 'relative' as const,
      zIndex: 5,
    },

    title: {
      fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
      fontWeight: 800,
      background: 'linear-gradient(135deg, #fff, #90caf9, #42a5f5)',
      backgroundSize: '300% 300%',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: `${oceanWaves} 6s ease infinite, ${neonPulse} 2s ease infinite`,
      marginBottom: '0.3rem',
      letterSpacing: '3px',
      textTransform: 'uppercase' as const,
      lineHeight: 1.1,
    },

    subtitle: {
      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
      background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
      backgroundSize: '200% 200%',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      fontStyle: 'italic',
      marginBottom: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
      fontWeight: 400,
      letterSpacing: '1px',
      animation: `${shimmerBlue} 4s ease infinite`,
    },

    progressWrapper: {
      position: 'relative' as const,
      width: { xs: '220px', sm: '250px', md: '280px' },
      margin: '0 auto',
      animation: `${fadeInUp} 0.8s ease-out 0.8s both`,
    },

    progressContainer: {
      width: '100%',
      height: '6px',
      background: 'rgba(33,150,243,0.1)',
      borderRadius: '3px',
      overflow: 'hidden',
      position: 'relative' as const,
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(33,150,243,0.2)',
      boxShadow: '0 0 20px rgba(33,150,243,0.2)',
    },

    progressFill: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      height: '100%',
      width: '0%',
      background: 'linear-gradient(90deg, #64b5f6, #1976d2)',
      backgroundSize: '200% 100%',
      animation: `${shimmerBlue} 1.5s linear infinite`,
      borderRadius: '3px',
      boxShadow: '0 0 15px rgba(33,150,243,0.6)',
    },

    progressGlow: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '100%',
      height: '150%',
      background: 'radial-gradient(circle, rgba(33,150,243,0.2) 0%, transparent 70%)',
      filter: 'blur(10px)',
      pointerEvents: 'none' as const,
    },

    particle: (particle: any) => ({
      position: 'absolute' as const,
      top: '100%',
      left: `${particle.left}%`,
      width: `${particle.size}px`,
      height: `${particle.size}px`,
      background: `rgba(33, 150, 243, ${particle.opacity})`,
      borderRadius: particle.type === 0 ? '50%' : 
                    particle.type === 1 ? '20% 50% 20% 50%' : 
                    particle.type === 2 ? '0%' : '50% 0% 50% 0%',
      transform: `rotate(${particle.rotation}deg)`,
      animation: `${floatParticles} ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) infinite ${particle.delay}s`,
      boxShadow: '0 0 20px currentColor',
      filter: `blur(${particle.type === 3 ? 1 : 0}px)`,
      opacity: 0.4,
    }),

    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
      marginTop: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
      animation: `${fadeInUp} 0.8s ease-out 1s both`,
      perspective: '1000px',
    },

    featureCard: (index: number) => ({
      textAlign: 'center' as const,
      padding: { xs: '0.6rem', sm: '0.8rem', md: '1rem' },
      background: 'rgba(33, 150, 243, 0.05)',
      borderRadius: '12px',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(33, 150, 243, 0.15)',
      transition: 'all 0.3s ease',
      transform: `rotateY(${index * 2}deg)`,
      '&:hover': {
        transform: 'rotateY(0deg) scale(1.05)',
        background: 'rgba(33, 150, 243, 0.1)',
        borderColor: 'rgba(33, 150, 243, 0.3)',
      },
      animation: `${float} ${4 + index}s ease-in-out infinite`,
    }),

    featureIcon: {
      fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
      marginBottom: '0.3rem',
      filter: 'drop-shadow(0 0 15px rgba(33,150,243,0.4))',
      animation: `${neonPulse} 2s ease-in-out infinite`,
    },

    featureText: {
      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
      color: 'rgba(255, 255, 255, 0.9)',
      fontWeight: 500,
      letterSpacing: '0.3px',
      textTransform: 'uppercase' as const,
      textShadow: '0 0 8px rgba(33,150,243,0.3)',
    },

    footer: {
      position: 'absolute' as const,
      bottom: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
      left: 0,
      width: '100%',
      textAlign: 'center' as const,
      animation: `${fadeIn} 0.8s ease-out 1.2s both`,
      zIndex: 10,
    },

    footerText: {
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
      fontWeight: 300,
      letterSpacing: '2px',
      textTransform: 'uppercase' as const,
      '& span': {
        color: '#64b5f6',
        fontWeight: 600,
        animation: `${neonPulse} 2s ease infinite`,
      },
    },
  };

  const features = [
    { icon: '🔷', text: 'Secure' },
    { icon: '⚡', text: 'Fast' },
    { icon: '🌀', text: 'Smart' },
  ];

  return (
    <Box sx={styles.container}>
      {/* Blue aurora overlays */}
      <Box sx={styles.aurora1} />
      <Box sx={styles.aurora2} />
      <Box sx={styles.gridOverlay} />
      
      {/* Premium blue particles */}
      {particles.map(particle => (
        <Box key={particle.id} sx={styles.particle(particle)} />
      ))}
      
      {/* Main Content */}
      <Box sx={styles.content}>
        {/* Welcome Section with Ripple Effect */}
        <Box sx={styles.welcomeSection}>
          <Box sx={styles.welcomeGlowRing} />
          <Typography variant="h1" sx={styles.welcomeText}>
            Welcome
          </Typography>
          {userName && (
            <Typography variant="h2" sx={styles.personalizedWelcome}>
              {userName}!
            </Typography>
          )}
          <Typography variant="body1" sx={styles.tagline}>
            Preparing your workspace
          </Typography>
        </Box>

        {/* Premium 3D Logo Section */}
        <Box sx={styles.logoSection}>
          <Box sx={styles.logoContainer}>
            <Box sx={styles.logoRing1} />
            <Box sx={styles.logoRing2} />
            <Box sx={styles.logoGlow} />
            <img
              src={logo}
              alt="DMS Logo"
              style={styles.logo}
            />
          </Box>
          
          <Typography variant="h1" sx={styles.title}>
            {/* DMS */}
            IDRS
          </Typography>
          
          <Typography variant="h6" sx={styles.subtitle}>
            {/* Document Management */}
            Institutional Digital Repository Software
          </Typography>
        </Box>
        
        {/* Premium Blue Progress Bar */}
        <Box sx={styles.progressWrapper}>
          <Box sx={styles.progressContainer}>
            <Box sx={styles.progressFill} />
          </Box>
          <Box sx={styles.progressGlow} />
        </Box>
        
        {/* Premium Feature Grid */}
        <Box sx={styles.featureGrid}>
          {features.map((feature, index) => (
            <Box key={index} sx={styles.featureCard(index)}>
              <Box sx={styles.featureIcon}>{feature.icon}</Box>
              <Typography variant="body2" sx={styles.featureText}>
                {feature.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      
      {/* Premium Footer */}
      <Box sx={styles.footer}>
        <Typography variant="body2" sx={styles.footerText}>
          <span>✦</span> PREMIUM EDITION <span>✦</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default PremiumBlueSplashScreen;
